import {
  Component,
  EventEmitter,
  OnInit,
  Output
} from '@angular/core';

import {
  FormBuilder,
  FormGroup,
  Validators
} from '@angular/forms';

import { GeneralService } from 'src/app/services/general.service';
import { CustomerService } from 'src/app/services/customer.service';
import { TarifasService } from 'src/app/services/tarifas.service';
import { EncriptacionService } from 'src/app/services/encriptacion.service';

@Component({
  selector: 'app-admin-descuentos',
  templateUrl: './admin-descuentos.component.html',
  styleUrls: ['./admin-descuentos.component.css']
})
export class AdminDescuentosComponent implements OnInit {

  // =========================================================
  // COMUNICACIÓN CON COMPONENTE PADRE
  // =========================================================

  @Output()
  cantidadDescuentosChange = new EventEmitter<number>();


  // =========================================================
  // DATA
  // =========================================================

  public usuario: any = {};

  public clientes: any[] = [];
  public planesCliente: any[] = [];

  public especialidadesList: any[] = [];

  public descuentos: any[] = [];

  public clienteSelected: any = null;

  public loadingDiscounts = false;
  public creatingCode = false;


  // =========================================================
  // FORMULARIO
  // =========================================================

  public dctoPost!: FormGroup;


  // =========================================================
  // BUSCADOR Y PAGINACIÓN
  // =========================================================

  public searchText = '';

  public currentPage = 1;

  public pageSize = 15;

  public pageSizeOptions: number[] = [
    15,
    25,
    50
  ];


  constructor(
    private fb: FormBuilder,
    public generalService: GeneralService,
    public customerService: CustomerService,
    public tarifasService: TarifasService
  ) {
    this.createForm();
  }


  ngOnInit(): void {

    this.usuario =
      new EncriptacionService()
        .getUserInfo();

    this.getSpecialityList();
    this.postListarCustomer();
    this.listDiscounts();
  }


  // =========================================================
  // FORM
  // =========================================================

  private createForm(): void {

    this.dctoPost = this.fb.group({

      /*
       * 1 = Medicina General
       * 2 = Especialidades
       */
      prestacion: [
        '1',
        Validators.required
      ],

      especialidades: [''],

      /*
       * 1 = Porcentaje
       * 2 = Monto fijo
       */
      descuento: [
        '2',
        Validators.required
      ],

      monto: [
        '',
        Validators.required
      ],

      /*
       * 1 = General
       * 2 = Por cliente
       */
      asignacion: [
        '1',
        Validators.required
      ],

      cliente: [''],

      plan: [''],

      fechaInicio: [
        '',
        Validators.required
      ],

      /*
       * En el nuevo diseño es opcional.
       */
      fechaLimite: [''],

      /*
       * Vacío o 0 = uso ilimitado.
       */
      limiteUso: [
        '',
        [
          Validators.min(0),
          Validators.max(10)
        ]
      ]

    });

    this.updateDiscountValidators();
    this.updateAssignmentValidators();
  }


  // =========================================================
  // HELPERS FORM
  // =========================================================

  public campoValido(
    campo: string
  ): boolean {

    const control =
      this.dctoPost.get(campo);

    return !!(
      control
      && control.invalid
      && control.touched
    );
  }


  public isMedicinaGeneral(): boolean {

    return String(
      this.dctoPost.get('prestacion')?.value
    ) === '1';
  }


  public isEspecialidades(): boolean {

    return String(
      this.dctoPost.get('prestacion')?.value
    ) === '2';
  }


  public isPercentageDiscount(): boolean {

    return String(
      this.dctoPost.get('descuento')?.value
    ) === '1';
  }


  public isFixedDiscount(): boolean {

    return String(
      this.dctoPost.get('descuento')?.value
    ) === '2';
  }


  public isGeneralAssignment(): boolean {

    return String(
      this.dctoPost.get('asignacion')?.value
    ) === '1';
  }


  public isClientAssignment(): boolean {

    return String(
      this.dctoPost.get('asignacion')?.value
    ) === '2';
  }


  // =========================================================
  // PRESTACIÓN
  // =========================================================

  public selectPrestacion(
    value: string
  ): void {

    this.dctoPost.patchValue({
      prestacion: value
    });

    if (value === '1') {

      this.dctoPost.patchValue({
        especialidades: ''
      });
    }
  }


  // =========================================================
  // TIPO DE DESCUENTO
  // =========================================================

  public selectDiscountType(
    value: string
  ): void {

    this.dctoPost.patchValue({
      descuento: value,
      monto: ''
    });

    this.updateDiscountValidators();
  }


  private updateDiscountValidators(): void {

    const control =
      this.dctoPost.get('monto');

    if (!control) {
      return;
    }

    if (this.isPercentageDiscount()) {

      control.setValidators([
        Validators.required,
        Validators.min(1),
        Validators.max(100)
      ]);

    } else {

      /*
       * Mantengo la regla del componente anterior.
       */
      control.setValidators([
        Validators.required,
        Validators.min(1000),
        Validators.max(100000)
      ]);
    }

    control.updateValueAndValidity({
      emitEvent: false
    });
  }


  public getDiscountValueHelp(): string {

    if (this.isPercentageDiscount()) {
      return 'Ingresa un porcentaje entre 1% y 100%.';
    }

    return 'Ingresa un monto entre $1.000 y $100.000.';
  }


  // =========================================================
  // ASIGNACIÓN
  // =========================================================

  public selectAssignment(
    value: string
  ): void {

    this.dctoPost.patchValue({
      asignacion: value
    });

    if (value === '1') {

      this.clienteSelected = null;
      this.planesCliente = [];

      this.dctoPost.patchValue({
        cliente: '',
        plan: ''
      });
    }

    this.updateAssignmentValidators();
  }


  private updateAssignmentValidators(): void {

    const cliente =
      this.dctoPost.get('cliente');

    const plan =
      this.dctoPost.get('plan');

    if (!cliente || !plan) {
      return;
    }

    if (this.isClientAssignment()) {

      cliente.setValidators([
        Validators.required
      ]);

      plan.setValidators([
        Validators.required
      ]);

    } else {

      cliente.clearValidators();
      plan.clearValidators();
    }

    cliente.updateValueAndValidity({
      emitEvent: false
    });

    plan.updateValueAndValidity({
      emitEvent: false
    });
  }


  // =========================================================
  // ESPECIALIDADES
  // =========================================================

  public getSpecialityList(): void {

    this.generalService
      .getSpecialityList()
      .then((data: any) => {

        this.especialidadesList =
          Array.isArray(data)
            ? data
            : [];

      })
      .catch((error: any) => {

        console.error(
          'Error getSpecialityList:',
          error
        );

        this.especialidadesList = [];
      });
  }


  // =========================================================
  // CLIENTES
  // =========================================================

  public postListarCustomer(): void {

    this.customerService
      .postListarCustomer({})
      .then((data: any) => {

        this.clientes =
          Array.isArray(data)
            ? data
            : [];

      })
      .catch((error: any) => {

        console.error(
          'Error postListarCustomer:',
          error
        );

        this.clientes = [];
      });
  }


  public changeClient(
    customerId: any
  ): void {

    this.clienteSelected = null;
    this.planesCliente = [];

    this.dctoPost.patchValue({
      plan: ''
    });

    if (!customerId) {
      return;
    }

    this.clienteSelected =
      this.clientes.find(
        (cliente: any) =>
          Number(cliente?.cus_id)
          === Number(customerId)
      ) || null;

    if (!this.clienteSelected) {
      return;
    }

    this.planesCliente =
      Array.isArray(
        this.clienteSelected?.planes
      )
        ? this.clienteSelected.planes
        : [];
  }


  // =========================================================
  // LISTAR CÓDIGOS
  // =========================================================

  public async listDiscounts(): Promise<void> {

    this.loadingDiscounts = true;

    try {

      const data: any =
        await this.tarifasService
          .listDiscounts();

      this.descuentos =
        Array.isArray(data)
          ? data
          : [];

      this.cantidadDescuentosChange.emit(
        this.descuentos.length
      );

      this.currentPage = 1;

    } catch (error) {

      console.error(
        'Error listDiscounts:',
        error
      );

      this.descuentos = [];

      this.cantidadDescuentosChange.emit(0);

    } finally {

      this.loadingDiscounts = false;
    }
  }


  // =========================================================
  // CREAR CÓDIGO
  // =========================================================

  public async createCode(): Promise<void> {

    this.updateDiscountValidators();
    this.updateAssignmentValidators();

    if (this.dctoPost.invalid) {

      this.dctoPost.markAllAsTouched();

      return;
    }

    if (this.creatingCode) {
      return;
    }

    const raw =
      this.dctoPost.getRawValue();

    /*
     * Mantiene la misma estructura que recibía
     * createDiscount() en el componente anterior.
     */
    const payload = {

      prestacion:
        raw.prestacion,

      especialidades:
        this.isEspecialidades()
          ? raw.especialidades
          : '',

      descuento:
        raw.descuento,

      monto:
        Number(raw.monto),

      asignacion:
        raw.asignacion,

      cliente:
        this.isClientAssignment()
          ? raw.cliente
          : '',

      plan:
        this.isClientAssignment()
          ? raw.plan
          : '',

      fechaInicio:
        raw.fechaInicio,

      /*
       * Nuevo diseño:
       * Fecha límite opcional.
       */
      fechaLimite:
        raw.fechaLimite || null,

      /*
       * 0 = ilimitado.
       */
      limiteUso:
        raw.limiteUso === ''
        || raw.limiteUso === null
        || raw.limiteUso === undefined
          ? 0
          : Number(raw.limiteUso)
    };


    this.creatingCode = true;

    this.generalService.modalCargando();

    try {

      const result: any =
        await this.tarifasService
          .createDiscount(payload);

      this.generalService.modalExitoso(
        'Código creado exitosamente',
        result?.code
          ? `Código ${result.code}`
          : 'Creado exitosamente'
      );

      this.resetForm();

      await this.listDiscounts();

    } catch (error: any) {

      console.error(
        'Error createDiscount:',
        error
      );

      this.generalService.modalError(
        'Error al generar código',
        error?.msg
          || 'Código no creado'
      );

    } finally {

      this.creatingCode = false;
    }
  }


  private resetForm(): void {

    this.clienteSelected = null;
    this.planesCliente = [];

    this.dctoPost.reset({

      prestacion: '1',

      especialidades: '',

      descuento: '2',

      monto: '',

      asignacion: '1',

      cliente: '',

      plan: '',

      fechaInicio: '',

      fechaLimite: '',

      limiteUso: ''
    });

    this.updateDiscountValidators();
    this.updateAssignmentValidators();
  }


  // =========================================================
  // BUSCADOR
  // =========================================================

  public onSearchChange(): void {

    this.currentPage = 1;
  }


  public get filteredDiscounts(): any[] {

    const search =
      (this.searchText || '')
        .trim()
        .toLowerCase();

    if (!search) {
      return [...this.descuentos];
    }

    return this.descuentos.filter(
      (discount: any) => {

        return [
          this.getDiscountCode(discount),
          this.getDiscountType(discount),
          this.getDiscountStatus(discount),
          this.getDiscountSpecialty(discount),
          this.getDiscountAssignment(discount),
          this.getDiscountResponsible(discount)
        ]
          .join(' ')
          .toLowerCase()
          .includes(search);
      }
    );
  }


  // =========================================================
  // PAGINACIÓN
  // =========================================================

  public get paginatedDiscounts(): any[] {

    const start =
      (this.currentPage - 1)
      * this.pageSize;

    return this.filteredDiscounts.slice(
      start,
      start + this.pageSize
    );
  }


  public get totalDiscounts(): number {

    return this.filteredDiscounts.length;
  }


  public get totalPages(): number {

    return Math.max(
      Math.ceil(
        this.totalDiscounts
        / this.pageSize
      ),
      1
    );
  }


  public get firstVisibleItem(): number {

    if (!this.totalDiscounts) {
      return 0;
    }

    return (
      (this.currentPage - 1)
      * this.pageSize
    ) + 1;
  }


  public get lastVisibleItem(): number {

    return Math.min(
      this.currentPage
      * this.pageSize,
      this.totalDiscounts
    );
  }


  public changePageSize(
    value: any
  ): void {

    this.pageSize =
      Number(value) || 15;

    this.currentPage = 1;
  }


  public firstPage(): void {

    this.currentPage = 1;
  }


  public previousPage(): void {

    if (this.currentPage > 1) {

      this.currentPage--;
    }
  }


  public nextPage(): void {

    if (
      this.currentPage
      < this.totalPages
    ) {

      this.currentPage++;
    }
  }


  public lastPage(): void {

    this.currentPage =
      this.totalPages;
  }


  // =========================================================
  // MAPEO DATA DESCUENTOS
  //
  // Centralizo los nombres para que cuando me pases una
  // respuesta real de listDiscounts solamente tengamos que
  // ajustar esta sección.
  // =========================================================

  public getDiscountId(
    discount: any
  ): any {

    return discount?.dis_id
      ?? discount?.dct_id
      ?? discount?.discount_id
      ?? discount?.id
      ?? '—';
  }


  public getDiscountCode(
    discount: any
  ): string {

    return discount?.dis_code
      || discount?.dct_code
      || discount?.discount_code
      || discount?.code
      || '—';
  }


  public getDiscountStartDate(
    discount: any
  ): string {

    const value =
      discount?.dis_start_date
      || discount?.dct_start_date
      || discount?.fecha_inicio
      || discount?.fechaInicio;

    return this.formatDate(value);
  }


  public getDiscountEndDate(
    discount: any
  ): string {

    const value =
      discount?.dis_end_date
      || discount?.dct_end_date
      || discount?.fecha_limite
      || discount?.fechaLimite;

    if (!value) {
      return 'Sin fecha';
    }

    return this.formatDate(value);
  }


  public getDiscountType(
    discount: any
  ): string {

    const value =
      discount?.dis_typ_name
      ?? discount?.discount_type
      ?? discount?.tipo_descuento
      ?? discount?.descuento;

    if (
      String(value) === '1'
      || this.normalizeText(value)
        .includes('PORCENTUAL')
    ) {

      return 'PORCENTUAL';
    }

    if (
      String(value) === '2'
      || this.normalizeText(value)
        .includes('MONTO')
    ) {

      return 'MONTO';
    }

    return value
      ? String(value)
      : '—';
  }


  public getDiscountValue(
    discount: any
  ): string {

    const value =
      Number(
        discount?.dis_value
        ?? discount?.discount_value
        ?? discount?.monto
        ?? discount?.valor_descuento
        ?? 0
      ) * 100;

    if (
      this.getDiscountType(discount)
      === 'PORCENTUAL'
    ) {

      return `${value}%`;
    }

    return this.formatPrice(value);
  }


  public getDiscountUses(
    discount: any
  ): number {

    return Number(
      discount?.dis_uses
      ?? discount?.uses
      ?? discount?.qty_use
      ?? discount?.usos
      ?? 0
    );
  }


  public getDiscountUseLimit(
    discount: any
  ): string {

    const limit =
      Number(
        discount?.dis_use_limit
        ?? discount?.limit_use
        ?? discount?.limiteUso
        ?? discount?.limite_uso
        ?? 0
      );

    return limit > 0
      ? String(limit)
      : 'Uso ilimitado';
  }


  public getDiscountSpecialty(
    discount: any
  ): string {

    return discount.specialities && discount.specialities[0]?.spe_name
      || discount?.sub_spe_name
      || discount?.specialty_name
      || discount?.especialidad
      || 'Medicina General';
  }


  public getDiscountAssignment(
    discount: any
  ): string {

    const value =
      discount?.assignment_name
      || discount?.asignacion_nombre
      || discount?.cus_name;

    if (value) {
      return String(value);
    }

    const type =
      discount?.assignment_type
      ?? discount?.asignacion;

    if (String(type) === '2') {
      return 'Por cliente';
    }

    return 'General';
  }


  public getDiscountResponsible(
    discount: any
  ): string {

    return discount?.responsible_name
      || discount?.use_full_name
      || discount?.created_by_name
      || discount?.responsable
      || '—';
  }


  // =========================================================
  // ESTADO
  // =========================================================

  public getDiscountStatus(
    discount: any
  ): string {

    /*
     * Primero respetamos un estado textual si el
     * backend ya lo entrega.
     */
    const explicitStatus =
      discount?.status_name
      || discount?.sta_name
      || discount?.discount_status_name;

    const normalized =
      this.normalizeText(explicitStatus);

    if (
      normalized.includes('expir')
      || normalized.includes('venc')
    ) {

      return 'Expirado';
    }

    if (
      normalized.includes('inactiv')
      || normalized.includes('desactiv')
    ) {

      return 'Inactivo';
    }

    if (
      normalized.includes('activ')
    ) {

      return 'Activo';
    }


    /*
     * Si no hay texto, verificamos fecha límite.
     */
    const endValue =
      discount?.dis_end_date
      || discount?.dct_end_date
      || discount?.fecha_limite
      || discount?.fechaLimite;

    const endDate =
      this.parseDate(endValue);

    if (endDate) {

      const today = new Date();

      today.setHours(
        0,
        0,
        0,
        0
      );

      if (endDate < today) {
        return 'Expirado';
      }
    }


    /*
     * Flags booleanos si existen.
     */
    const activeFlag =
      discount?.is_active
      ?? discount?.active;

    if (activeFlag === false) {
      return 'Inactivo';
    }

    return 'Activo';
  }


  public getDiscountStatusClass(
    discount: any
  ): string {

    const status =
      this.getDiscountStatus(discount);

    if (status === 'Expirado') {
      return 'discount-status-expired';
    }

    if (status === 'Inactivo') {
      return 'discount-status-inactive';
    }

    return 'discount-status-active';
  }


  // =========================================================
  // ACCIONES
  // =========================================================

  public editDiscount(
    discount: any
  ): void {

    /*
     * El componente antiguo no posee endpoint para editar
     * códigos de descuento.
     *
     * Dejamos preparada la acción.
     */
    console.log(
      'Editar descuento:',
      discount
    );
  }


  public toggleDiscountStatus(
    discount: any
  ): void {

    /*
     * El código anterior tampoco posee endpoint
     * activar/desactivar.
     *
     * Cuando me pases el servicio lo conectamos aquí.
     */
    console.log(
      'Cambiar estado descuento:',
      discount
    );
  }


  public getStatusActionLabel(
    discount: any
  ): string {

    return this.getDiscountStatus(discount)
      === 'Activo'
        ? 'Desactivar'
        : 'Activar';
  }


  // =========================================================
  // EXPORTAR
  // =========================================================

  public exportDiscountsToExcel(): void {

    /*
     * Sin agregar librerías externas.
     * Generamos CSV compatible con Excel.
     */

    const rows = this.filteredDiscounts.map(
      (discount: any) => [

        this.getDiscountId(discount),

        this.getDiscountCode(discount),

        this.getDiscountStartDate(discount),

        this.getDiscountEndDate(discount),

        this.getDiscountStatus(discount),

        this.getDiscountType(discount),

        this.getDiscountValue(discount),

        this.getDiscountUses(discount),

        this.getDiscountUseLimit(discount),

        this.getDiscountSpecialty(discount),

        this.getDiscountAssignment(discount),

        this.getDiscountResponsible(discount)
      ]
    );


    const headers = [
      'ID',
      'Código',
      'Fecha inicio',
      'Fecha límite',
      'Estado',
      'Tipo descuento',
      'Valor descuento',
      'Usos',
      'Límite de uso',
      'Sub especialidad',
      'Asignación',
      'Responsable'
    ];


    const content = [
      headers,
      ...rows
    ]
      .map((row: any[]) =>
        row
          .map((value: any) =>
            `"${String(value ?? '')
              .replace(/"/g, '""')}"`
          )
          .join(';')
      )
      .join('\n');


    const blob =
      new Blob(
        [
          '\uFEFF',
          content
        ],
        {
          type:
            'text/csv;charset=utf-8;'
        }
      );


    const url =
      URL.createObjectURL(blob);

    const link =
      document.createElement('a');

    link.href = url;

    link.download =
      `codigos-descuento-${new Date()
        .toISOString()
        .slice(0, 10)}.csv`;

    link.click();

    URL.revokeObjectURL(url);
  }


  // =========================================================
  // FORMATOS
  // =========================================================

  private normalizeText(
    value: any
  ): string {

    return (value || '')
      .toString()
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(
        /[\u0300-\u036f]/g,
        ''
      );
  }


  private parseDate(
    value: any
  ): Date | null {

    if (!value) {
      return null;
    }

    if (value instanceof Date) {

      return isNaN(
        value.getTime()
      )
        ? null
        : value;
    }

    const raw =
      value.toString();

    const iso =
      raw.match(
        /^(\d{4})-(\d{2})-(\d{2})/
      );

    if (iso) {

      return new Date(
        Number(iso[1]),
        Number(iso[2]) - 1,
        Number(iso[3])
      );
    }

    const latin =
      raw.match(
        /^(\d{2})[-/](\d{2})[-/](\d{4})$/
      );

    if (latin) {

      return new Date(
        Number(latin[3]),
        Number(latin[2]) - 1,
        Number(latin[1])
      );
    }

    const parsed =
      new Date(raw);

    return isNaN(
      parsed.getTime()
    )
      ? null
      : parsed;
  }


  public formatDate(
    value: any
  ): string {

    const date =
      this.parseDate(value);

    if (!date) {
      return '—';
    }

    const day =
      String(
        date.getDate()
      ).padStart(2, '0');

    const month =
      String(
        date.getMonth() + 1
      ).padStart(2, '0');

    return `${day}-${month}-${date.getFullYear()}`;
  }


  public formatPrice(
    value: any
  ): string {

    const number =
      Number(value || 0);

    return number
      .toLocaleString(
        'es-CL',
        {
          style: 'currency',
          currency: 'CLP',
          maximumFractionDigits: 0
        }
      );
  }

}